/* eslint-disable no-nested-ternary */
import { FC } from 'react';
import { Modal, Button, List, Avatar, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTypedTranslation } from 'translations/typedTranslations';
import { ModalBody } from 'utils/ModalStyle';
import { CloseButton } from 'utils/CloseButton';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';

import { Maybe, Users, Users_Cuentas } from 'generated/graphql';
import { TitleSwarm } from 'utils/TextFormats';
import { WhiteContainerSide } from 'utils/Containers';
import { TipoUsuarioEnum } from '../../../enums/tipoUsuario';

interface IAccountSelectionModalProps {
	visible: boolean;
	accounts: {
		profesor?: Array<{ hasuraKey: string }>;
		monitor?: Array<{ hasuraKey: string }>;
		coordinador?: Array<{ hasuraKey: string }>;
		user?: { hasuraKey: string };
	};
	onAccountSelect: (accountType: string, account: any) => void;
	onCancel: () => void;
	currentUser: TipoUsuarioEnum;
	userKey?: string;
}

export interface IUserItem {
	hasuraKey: string;
	nombre: string;
	apellido: string;
	avatarPath: Maybe<string> | undefined;
	colegio: string | undefined;
	institucion: string | undefined;
	rol: string;
	tipoUsuario: string;
	colegioKey: string | undefined;
	institucionKey: string | undefined;
	userKey: string | undefined;
	firebase_uid: Maybe<string> | undefined;
	email: Maybe<string> | undefined;
}

const GET_ACCOUNT_DATA_BY_KEY = gql`
	query getAccountDataByKey($userKey: uuid!, $listaKeys: [uuid!]!, $currentUser: String!) {
		users(where: { key: { _eq: $userKey } }) {
			key
			nombre
			apellido
			avatarPath
		}
		users_cuentas(where: { key: { _in: $listaKeys }, tipoUsuario: { _eq: $currentUser } }) {
			key
			rol
			tipoUsuario
			firebase_uid
			email
			institucion {
				key
				nombre
			}
			colegio {
				key
				nombre
			}
		}
	}
`;

const AccountSelectionModal: FC<IAccountSelectionModalProps> = ({
	visible,
	accounts,
	onAccountSelect,
	onCancel,
	currentUser,
	userKey
}) => {
	const { t } = useTypedTranslation();

	const keys =
		currentUser === TipoUsuarioEnum.monitor
			? accounts.monitor?.flatMap((monitor) => monitor.hasuraKey)
			: currentUser === TipoUsuarioEnum.profesor
			? accounts.profesor?.flatMap((profesor) => profesor.hasuraKey)
			: accounts.coordinador?.flatMap((coordinador) => coordinador.hasuraKey) || [];

	const { data, loading, error } = useQuery<{ users: Users[]; users_cuentas: Users_Cuentas[] }>(
		GET_ACCOUNT_DATA_BY_KEY,
		{
			variables: {
				userKey: accounts.user?.hasuraKey ?? userKey,
				listaKeys: keys,
				currentUser
			}
		}
	);

	console.log('data user y cuentas', currentUser, accounts, userKey, {
		userKey: accounts.user?.hasuraKey ?? userKey,
		listaKeys: keys,
		currentUser
	});

	console.log(data, loading, error);
	if (loading || error || !data) {
		return (
			<Modal
				title={t('Cargando...')}
				visible={visible}
				mask
				closable
				destroyOnClose
				width={600}
				closeIcon={<CloseButton onClick={onCancel} />}
				footer={[]}
			/>
		);
	}

	// Get user data for the title/subtitle
	const userData = data.users?.[0];

	// Get user data for the title/subtitle
	const accountList = data.users_cuentas.map((cuenta) => {
		const { key, rol, tipoUsuario, colegio, institucion, email } = cuenta;
		return {
			hasuraKey: key,
			tipoUsuario,
			nombre: userData?.nombre || '',
			apellido: userData?.apellido || '??',
			avatarPath: userData?.avatarPath,
			colegio: colegio?.nombre,
			institucion: institucion?.nombre,
			colegioKey: colegio?.key,
			institucionKey: institucion?.key,
			rol: rol ?? 'undefined',
			userKey: userData?.key,
			firebase_uid: cuenta.firebase_uid,
			email,
			nombreCargo:
				tipoUsuario === TipoUsuarioEnum.monitor
					? t('Monitor')
					: tipoUsuario === TipoUsuarioEnum.profesor
					? t('Profesor')
					: t('Coordinador')
		};
	});

	const modalTitle = (
		<>
			{userData && (
				<div style={{ marginTop: 8, display: 'flex', alignItems: 'center' }}>
					<Avatar
						src={userData.avatarPath}
						size={32}
						icon={!userData.avatarPath && <UserOutlined />}
						style={{ marginRight: 12 }}
					/>
					<div>
						<Typography.Text strong style={{ fontSize: 16 }}>
							{userData.nombre} {userData.apellido || '??'}
						</Typography.Text>
					</div>
				</div>
			)}
		</>
	);

	const handleSelectAccount = (account: IUserItem) => {
		onAccountSelect(account.tipoUsuario, account);
	};

	return (
		<ModalBody
			title={<TitleSwarm className="mt-2">{t('Selecciona tu cuenta')}</TitleSwarm>}
			visible={visible}
			mask
			closable
			destroyOnClose
			width={500}
			closeIcon={<CloseButton onClick={onCancel} />}
			footer={[]}
		>
			<WhiteContainerSide>
				{modalTitle}
				<List
					itemLayout="horizontal"
					dataSource={accountList}
					renderItem={(item) => (
						<List.Item
							actions={[
								<Button key="select" type="primary" onClick={() => handleSelectAccount(item)}>
									{t('Ingresar')}
								</Button>
							]}
						>
							<List.Item.Meta
								title={
									<Typography.Text strong style={{ fontSize: 16, display: 'block' }}>
										{item.nombreCargo}
									</Typography.Text>
								}
								description={
									<Typography.Text type="secondary">
										{item.tipoUsuario === TipoUsuarioEnum.monitor
											? `${t('Institución')}: ${item.institucion}`
											: item.colegio
											? `${t('Colegio')}: ${item.colegio}`
											: 'Usuario libre'}
									</Typography.Text>
								}
							/>
						</List.Item>
					)}
				/>
			</WhiteContainerSide>
		</ModalBody>
	);
};

export default AccountSelectionModal;
